import axios from "axios";

/**
 * Backend URL resolution — robust against missing build-time env var.
 *
 * Priority:
 *   1. REACT_APP_BACKEND_URL (baked at build time by CRA / Railway env)
 *   2. window.__BACKEND_URL__ (runtime override injected in index.html, if needed)
 *   3. Same-origin (works when frontend & backend share a domain via reverse proxy)
 *
 * IMPORTANT: NO hardcoded production URL fallback. Hardcoding bites us
 * when a backend rotates URLs (Railway re-provision, env split, etc.)
 * and the frontend silently keeps talking to the OLD one — producing
 * white-screen / "logged-out forever" bugs that are hell to diagnose.
 * If the env var is missing in any environment, we'd rather show a clear
 * "API offline" error than mislead the user with stale data.
 */
function resolveBackendUrl() {
  const fromEnv = process.env.REACT_APP_BACKEND_URL;
  // Only trust env var if it's a properly formatted absolute URL.
  // Avoids bug where the env was set without "https://" prefix and
  // axios treated it as a relative path, hitting the frontend itself.
  if (fromEnv && /^https?:\/\//i.test(fromEnv)) {
    return fromEnv.replace(/\/$/, "");
  }

  if (typeof window !== "undefined" && window.__BACKEND_URL__) {
    return window.__BACKEND_URL__.replace(/\/$/, "");
  }

  // Last resort: same origin (the deployment must reverse-proxy /api/*
  // to the backend service). If neither env var nor proxy is set up,
  // network calls will fail loudly — by design.
  return "";
}

const BACKEND_URL = resolveBackendUrl();
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("agentos_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    // v0.99.8 · Fev/2026 — NÃO redireciona pra /login em 401 do endpoint
    // de login em si. Se a senha estiver errada, a página de login (gov
    // ou privado) DEVE mostrar o toast de erro no lugar, sem sair da URL.
    // Antes, quando o usuário digitava senha errada em /gov/login, o
    // interceptor global fazia window.location.href = "/login" (a tela
    // privada), o que confundia o operador e quebrava a promessa da
    // vertical GOV isolada.
    const url = (err?.config?.url || "").toString();
    const isLoginCall = /\/auth\/(login|register)/.test(url);
    if (status === 401 && !isLoginCall) {
      localStorage.removeItem("agentos_token");
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    // Credit-exhausted blocks come back as HTTP 402 with a structured
    // detail body. We surface a global `agentos:credits_exhausted` event
    // so any mounted <NoCreditsModal /> can pop instantly — without each
    // calling component having to handle the rejection themselves.
    if (status === 402 && err?.response?.data?.detail?.error === "credits_exhausted") {
      try {
        window.dispatchEvent(new CustomEvent("agentos:credits_exhausted", {
          detail: err.response.data.detail,
        }));
      } catch {
        // no-op: CustomEvent is universally supported in modern browsers
      }
    }
    // Pydantic v2 returns `detail` as a list of {type, loc, msg, input, ctx}
    // when validation fails (HTTP 422). Many callers drop that straight into
    // `toast.error(...)` or into JSX — both crash React with "Objects are
    // not valid as a React child". Coerce it back to a single PT-BR string
    // here so EVERY call site is safe by default.
    const raw = err?.response?.data?.detail;
    if (Array.isArray(raw)) {
      try {
        const human = raw
          .map((e) => {
            const field = Array.isArray(e?.loc) ? e.loc.filter((x) => x !== "body").join(".") : "";
            const msg = e?.msg || "valor inválido";
            return field ? `${field}: ${msg}` : msg;
          })
          .join(" · ");
        if (err.response) {
          err.response.data = { ...err.response.data, detail: human };
        }
      } catch {
        // fall back to a generic message — never let the interceptor itself throw
        if (err.response) err.response.data = { detail: "Dados inválidos" };
      }
    }
    return Promise.reject(err);
  }
);
